import {useDebugValue} from 'react';

import {useSystemOptions} from './useSystemOptions';

import {useMediaActions} from './useMediaActions';
import {useMediaEvents} from './useMediaEvents';
import {useMediaOptions} from './useMediaOptions';
import {useMediaSettings} from './useMediaSettings';

import {useAbacusStore} from './useAbacusStore';

import {useEdapOptions} from './useEdapOptions';

import {useSearchActions} from './useSearchActions';
import {useSearchCookies} from './useSearchCookies';
import {useSearchEvents} from './useSearchEvents';
import {useSearchValues} from './useSearchValues';

import {useFormActions} from './useFormActions';
import {useFormEvents} from './useFormEvents';
import {useFormOptions} from './useFormOptions';
import {useFormSettings} from './useFormSettings';

import {useDestinationActions} from './useDestinationActions';
import {useDestinationEvents} from './useDestinationEvents';
import {useDestinationOptions} from './useDestinationOptions';
import {useDestinationSettings} from './useDestinationSettings';
import {useDestinationTranslations} from './useDestinationTranslations';
import {useDestinationValues} from './useDestinationValues';

import {useDestinationSummaryEvents} from './useDestinationSummaryEvents';

import {useDatesActions} from './useDatesActions';
import {useDatesEvents} from './useDatesEvents';
import {useDatesOptions} from './useDatesOptions';
import {useDatesSettings} from './useDatesSettings';
import {useDatesTranslations} from './useDatesTranslations';
import {useDatesValues} from './useDatesValues';

import {useGuestsActions} from './useGuestsActions';
import {useGuestsEvents} from './useGuestsEvents';
import {useGuestsOptions} from './useGuestsOptions';
import {useGuestsSettings} from './useGuestsSettings';
import {useGuestsTranslations} from './useGuestsTranslations';
import {useGuestsValues} from './useGuestsValues';

import {useSubmitEvents} from './useSubmitEvents';
import {useSubmitOptions} from './useSubmitOptions';
import {useSubmitTranslations} from './useSubmitTranslations';

import {useSubmitSummaryEvents} from './useSubmitSummaryEvents';

import {useSubmitDetailsEvents} from './useSubmitDetailsEvents';

import {useDetailsModalActions} from './useDetailsModalActions';
import {useDetailsModalEvents} from './useDetailsModalEvents';
import {useDetailsModalOptions} from './useDetailsModalOptions';
import {useDetailsModalSettings} from './useDetailsModalSettings';
import {useDetailsModalTranslations} from './useDetailsModalTranslations';

function useSearchFormContext(props) {
    const {
        systemOptions
    } = useSystemOptions(props);

    const {
        mediaEvents
    } = useMediaEvents(props);

    const {
        mediaOptions
    } = useMediaOptions(props);

    const {
        initialMediaSettings,
        mediaSettings,
        changeMediaSettings
    } = useMediaSettings(props);

    useMediaActions({
        mediaEvents,
        mediaOptions,
        initialMediaSettings,
        changeMediaSettings
    });

    const {
        abacusStore
    } = useAbacusStore(props);

    const {
        edapOptions
    } = useEdapOptions(props);

    const {
        searchCookies
    } = useSearchCookies();

    const {
        searchEvents
    } = useSearchEvents(props);

    const {
        initialSearchValues,
        currentSearchValues,
        latestSearchValues,
        setAllSearchValuesInitial
    } = useSearchValues(props);

    useSearchActions({
        searchEvents,
        initialSearchValues,
        currentSearchValues,
        latestSearchValues,
        setAllSearchValuesInitial
    });

    const {
        formEvents
    } = useFormEvents(props);

    const {
        formOptions
    } = useFormOptions(props, abacusStore);

    const {
        initialFormSettings,
        formSettings,
        changeFormSettings
    } = useFormSettings(props);

    const formActions = useFormActions({
        formEvents,
        initialFormSettings,
        changeFormSettings
    });

    const {
        destinationEvents
    } = useDestinationEvents(props);

    const {
        destinationOptions
    } = useDestinationOptions(props);

    const {
        initialDestinationSettings,
        destinationSettings,
        changeDestinationSettings
    } = useDestinationSettings(props);

    const {
        destinationTranslations
    } = useDestinationTranslations(props);

    const {
        initialDestinationValues,
        currentDestinationValues,
        latestDestinationValues,
        setAllDestinationValues,
        setAllDestinationValuesInitial
    } = useDestinationValues(props, searchCookies, systemOptions, abacusStore);

    const destinationActions = useDestinationActions({
        destinationEvents,
        initialDestinationSettings,
        changeDestinationSettings,
        initialDestinationValues,
        currentDestinationValues,
        latestDestinationValues,
        setAllDestinationValues,
        setAllDestinationValuesInitial
    });

    const {
        destinationSummaryEvents
    } = useDestinationSummaryEvents(props);

    const {
        datesEvents
    } = useDatesEvents(props);

    const {
        datesOptions
    } = useDatesOptions(props, abacusStore);

    const {
        initialDatesSettings,
        datesSettings,
        changeDatesSettings
    } = useDatesSettings(props, systemOptions, abacusStore);

    const {
        datesTranslations
    } = useDatesTranslations(props);

    const {
        initialDatesValues,
        currentDatesValues,
        latestDatesValues,
        setCurrentDatesValuesDefault,
        setCurrentDatesValuesLatest,
        setLatestDatesValues,
        setAllDatesValuesDefault,
        setAllDatesValuesInitial
    } = useDatesValues(props, searchCookies, systemOptions, abacusStore);

    const datesActions = useDatesActions({
        datesEvents,
        initialDatesSettings,
        changeDatesSettings,
        initialDatesValues,
        currentDatesValues,
        latestDatesValues,
        setCurrentDatesValuesDefault,
        setCurrentDatesValuesLatest,
        setLatestDatesValues,
        setAllDatesValuesDefault,
        setAllDatesValuesInitial
    });

    const {
        guestsEvents
    } = useGuestsEvents(props);

    const {
        guestsOptions
    } = useGuestsOptions(props, abacusStore);

    const {
        initialGuestsSettings,
        guestsSettings,
        changeGuestsSettings
    } = useGuestsSettings(props);

    const {
        guestsTranslations
    } = useGuestsTranslations(props);

    const {
        initialGuestsValues,
        currentGuestsValues,
        latestGuestsValues,
        setCurrentGuestsValuesLatest,
        setLatestGuestsValues,
        setAllGuestsValuesDefault,
        setAllGuestsValuesInitial
    } = useGuestsValues(props, searchCookies, systemOptions, abacusStore);

    const guestsActions = useGuestsActions({
        guestsEvents,
        initialGuestsSettings,
        changeGuestsSettings,
        initialGuestsValues,
        currentGuestsValues,
        latestGuestsValues,
        setCurrentGuestsValuesLatest,
        setLatestGuestsValues,
        setAllGuestsValuesDefault,
        setAllGuestsValuesInitial
    });

    const {
        submitEvents
    } = useSubmitEvents(props);

    const {
        submitOptions
    } = useSubmitOptions(props);

    const {
        submitTranslations
    } = useSubmitTranslations(props);

    const {
        submitSummaryEvents
    } = useSubmitSummaryEvents(props);

    const {
        submitDetailsEvents
    } = useSubmitDetailsEvents(props);

    const {
        detailsModalEvents
    } = useDetailsModalEvents(props);

    const {
        detailsModalOptions
    } = useDetailsModalOptions(props);

    const {
        initialDetailsModalSettings,
        detailsModalSettings,
        changeDetailsModalSettings
    } = useDetailsModalSettings(props);

    const {
        detailsModalTranslations
    } = useDetailsModalTranslations(props);

    const detailsModalActions = useDetailsModalActions({
        detailsModalEvents,
        initialDetailsModalSettings,
        changeDetailsModalSettings
    });

    const result = {
        systemOptions,
        mediaEvents,
        mediaOptions,
        mediaSettings,
        abacusStore,
        edapOptions,
        searchEvents,
        currentSearchValues,
        latestSearchValues,
        formActions,
        formEvents,
        formOptions,
        formSettings,
        destinationActions,
        destinationEvents,
        destinationOptions,
        destinationSettings,
        destinationTranslations,
        currentDestinationValues,
        latestDestinationValues,
        destinationSummaryEvents,
        datesActions,
        datesEvents,
        datesOptions,
        datesSettings,
        datesTranslations,
        currentDatesValues,
        latestDatesValues,
        guestsActions,
        guestsEvents,
        guestsOptions,
        guestsSettings,
        guestsTranslations,
        currentGuestsValues,
        latestGuestsValues,
        submitEvents,
        submitOptions,
        submitTranslations,
        submitSummaryEvents,
        submitDetailsEvents,
        detailsModalActions,
        detailsModalEvents,
        detailsModalOptions,
        detailsModalSettings,
        detailsModalTranslations
    };

    useDebugValue(result);

    return result;
}

export {useSearchFormContext};
